/**
 * Page titles for routes whose server layout can only give a SECTION title (WCAG 2.4.2, Q-2214).
 *
 * A "use client" page cannot export metadata, so the ~100 pages under a section layout all shared one title.
 * This table is the single source for the more specific title; `RouteTitle` (components/shared) applies it on
 * every navigation. Titles read "<page> · <section>"; the root metadata template adds "· EngageIQ".
 * Section INDEX pages are not listed: they keep their layout's own title, which is already unique.
 */
export const ROUTE_TITLES: Readonly<Record<string, string>> = {
  '/adminpanel/access-control/api-keys/[id]': 'API key details · Access control',
  '/adminpanel/access-control/api-keys/new': 'New API key · Access control',
  '/adminpanel/access-control/api-keys': 'API keys · Access control',
  '/adminpanel/access-control/audit': 'Audit · Access control',
  '/adminpanel/access-control/diagnostics/effective-permissions': 'Effective permissions diagnostic · Access control',
  '/adminpanel/access-control/diagnostics/inheritance-tree': 'Inheritance tree diagnostic · Access control',
  '/adminpanel/access-control/diagnostics': 'Diagnostics · Access control',
  '/adminpanel/access-control/diagnostics/role-comparison': 'Role comparison diagnostic · Access control',
  '/adminpanel/access-control/diagnostics/sync-checker': 'Sync checker diagnostic · Access control',
  '/adminpanel/access-control/invitations': 'Invitations · Access control',
  '/adminpanel/access-control/permissions': 'Permissions · Access control',
  '/adminpanel/access-control/roles/[id]': 'Role details · Access control',
  '/adminpanel/access-control/roles/new': 'New role · Access control',
  '/adminpanel/access-control/roles': 'Roles · Access control',
  '/adminpanel/access-control/team': 'Team · Access control',
  '/adminpanel/access-control/templates/[id]': 'Template details · Access control',
  '/adminpanel/access-control/templates': 'Templates · Access control',
  '/adminpanel/access-control/tiers': 'Tiers · Access control',
  '/adminpanel/access-control/users/[userId]': 'User details · Access control',
  '/adminpanel/access-control/users/create': 'Create user · Access control',
  '/adminpanel/access-control/users/invite': 'Invite user · Access control',
  '/adminpanel/access-control/users': 'Users · Access control',
  '/adminpanel/call-metrics/activity': 'Activity · Call metrics',
  '/adminpanel/call-metrics/ai-notes': 'AI notes · Call metrics',
  '/adminpanel/call-metrics/ai-performance': 'AI performance · Call metrics',
  '/adminpanel/call-metrics/alerts': 'Alerts · Call metrics',
  '/adminpanel/call-metrics/call-quality': 'Call quality · Call metrics',
  '/adminpanel/call-metrics/canvas': 'Canvas · Call metrics',
  '/adminpanel/call-metrics/connections': 'Connections · Call metrics',
  '/adminpanel/call-metrics/duration': 'Duration · Call metrics',
  '/adminpanel/call-metrics/entities': 'Entities · Call metrics',
  '/adminpanel/call-metrics/errors': 'Errors · Call metrics',
  '/adminpanel/call-metrics/feedback': 'Feedback · Call metrics',
  '/adminpanel/call-metrics/file-browser': 'File browser · Call metrics',
  '/adminpanel/call-metrics/latency': 'Latency · Call metrics',
  '/adminpanel/call-metrics/map': 'Map · Call metrics',
  '/adminpanel/call-metrics/notes': 'Notes · Call metrics',
  '/adminpanel/call-metrics/orchestrator': 'Orchestrator · Call metrics',
  '/adminpanel/call-metrics/outcomes': 'Outcomes · Call metrics',
  '/adminpanel/call-metrics/realtime': 'Realtime · Call metrics',
  '/adminpanel/call-metrics/report-quality': 'Report quality · Call metrics',
  '/adminpanel/call-metrics/reports': 'Reports · Call metrics',
  '/adminpanel/call-metrics/resources': 'Resources · Call metrics',
  '/adminpanel/call-metrics/settings': 'Settings · Call metrics',
  '/adminpanel/call-metrics/suggested-actions': 'Suggested actions · Call metrics',
  '/adminpanel/call-metrics/surveys': 'Surveys · Call metrics',
  '/adminpanel/call-metrics/throughput': 'Throughput · Call metrics',
  '/adminpanel/call-metrics/timeline': 'Timeline · Call metrics',
  '/adminpanel/call-metrics/transcription': 'Transcription · Call metrics',
  '/adminpanel/call-metrics/trends': 'Trends · Call metrics',
  '/adminpanel/call-metrics/version-regression': 'Version regression · Call metrics',
  '/adminpanel/call-metrics/video': 'Video · Call metrics',
  '/adminpanel/call-metrics/volume': 'Volume · Call metrics',
  '/adminpanel/deep-analysis/import-export': 'Import and export · Deep analysis',
  '/adminpanel/deep-analysis/scoring': 'Scoring · Deep analysis',
  '/adminpanel/deep-analysis/tests/[key]': 'Test details · Deep analysis',
  '/adminpanel/llm-metrics/alerts': 'Alerts · LLM metrics',
  '/adminpanel/llm-metrics/conversations': 'Conversations · LLM metrics',
  '/adminpanel/llm-metrics/cost-explorer/[callId]': 'Call cost details · LLM metrics',
  '/adminpanel/llm-metrics/cost-explorer': 'Cost explorer · LLM metrics',
  '/adminpanel/llm-metrics/costs': 'Costs · LLM metrics',
  '/adminpanel/llm-metrics/errors': 'Errors · LLM metrics',
  '/adminpanel/llm-metrics/latency': 'Latency · LLM metrics',
  '/adminpanel/llm-metrics/models': 'Models · LLM metrics',
  '/adminpanel/llm-metrics/realtime': 'Realtime · LLM metrics',
  '/adminpanel/llm-metrics/reconciliation': 'Reconciliation · LLM metrics',
  '/adminpanel/llm-metrics/requests': 'Requests · LLM metrics',
  '/adminpanel/llm-metrics/throughput': 'Throughput · LLM metrics',
  '/adminpanel/llm-metrics/unit-economics': 'Unit economics · LLM metrics',
  '/adminpanel/navigation/groups': 'Groups · Navigation settings',
  '/adminpanel/navigation/items/[key]': 'Navigation item details · Navigation settings',
  '/adminpanel/navigation/items': 'Items · Navigation settings',
  '/adminpanel/navigation/overrides': 'Overrides · Navigation settings',
  '/adminpanel/organizations/[id]': 'Organization details · Organizations',
  '/adminpanel/organizations/create': 'Create organization · Organizations',
  '/adminpanel/organizations/levels': 'Levels · Organizations',
  '/adminpanel/runbooks/[name]': 'Runbook details · Runbooks',
  '/developer/billing': 'Billing · Developer portal',
  '/developer/keys': 'API keys · Developer portal',
  '/developer/keys/new': 'New API key · Developer portal',
  '/developer/metrics': 'Metrics · Developer portal',
  '/developer/metrics/conversations': 'Conversation metrics · Developer portal',
  '/developer/metrics/costs': 'Cost metrics · Developer portal',
  '/developer/metrics/requests': 'Request metrics · Developer portal',
  '/developer/usage': 'Usage · Developer portal',
  '/citizen/help': 'Help · Citizen portal',
  '/citizen/login': 'Sign in · Citizen portal',
  '/citizen/register': 'Create account · Citizen portal',
  '/citizen/verify-email': 'Verify your email · Citizen portal',
  '/demo/[scenarioId]': 'Demo scenario',
  '/settings/ui/notifications': 'Notification settings',
  '/egress-layout': 'Egress layout',
};

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const MATCHERS = Object.entries(ROUTE_TITLES)
  .map(([pattern, title]) => {
    const segments = pattern.split('/').filter(Boolean);
    const dynamic = segments.filter((s) => s.startsWith('[')).length;
    const re = new RegExp(
      '^/' + segments.map((s) => (s.startsWith('[') ? '[^/]+' : escapeRe(s))).join('/') + '/?$',
    );
    return { re, title, dynamic };
  })
  // static routes win over dynamic ones: /roles/new before /roles/[id]
  .sort((a, b) => a.dynamic - b.dynamic);

/** The specific title for a concrete pathname, or null when the route's own server title already says it. */
export function resolveRouteTitle(pathname: string | null | undefined): string | null {
  if (!pathname) return null;
  const path = pathname.split(/[?#]/)[0];
  return MATCHERS.find((m) => m.re.test(path))?.title ?? null;
}
